import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Select } from '@shared/ui';
import useProviders from '../model/useProviders';

// Option label: "{title} {name}", with the NPI appended as a searchable
// suffix (the shared Select searches String(option.label), so the NPI
// must be part of the label string to stay findable).
const formatProviderLabel = provider => {
  const name = [provider.providerTitle, provider.name].filter(Boolean).join(' ');
  return provider.providerNPINumber ? `${name} · ${provider.providerNPINumber}` : name;
};

/**
 * Account-level provider picker on GET /accounts/{uuid}/providers.
 *
 * Selection travels as the row `uuid` (the one identifier the API
 * accepts everywhere); `onChange(uuid, provider)` also hands the full
 * row to callers that need more than the id. When `autoSelectDefault`
 * is set and no value is chosen yet, the account's default provider is
 * picked automatically once the list arrives — `defaultContext` says
 * WHICH default, since an account keeps one per context (Pdf / Claims /
 * Surveys). Without it nothing is preselected.
 */
const ProviderSelect = ({
  accountUuid,
  value = '',
  onChange,
  clearable = false,
  autoSelectDefault = false,
  defaultContext = null,
  label = null,
  disabled = false,
  skip = false,
}) => {
  const { providers: i18n } = useSelector(state => state.i18n);
  const { providers, defaultProvider, loading, error } = useProviders(accountUuid, {
    skip,
    defaultContext,
  });

  // Fires at most once per accountUuid load — a user who explicitly
  // cleared the field must not have the default forced back in.
  const autoSelectedRef = useRef(false);
  useEffect(() => {
    autoSelectedRef.current = false;
  }, [accountUuid]);
  useEffect(() => {
    if (!autoSelectDefault || autoSelectedRef.current || loading) return;
    if (!value && defaultProvider) {
      autoSelectedRef.current = true;
      onChange(defaultProvider.uuid, defaultProvider);
    }
  }, [autoSelectDefault, loading, value, defaultProvider, onChange]);

  const options = [
    ...(clearable ? [{ value: '', label: i18n.noProvider }] : []),
    ...providers.map(provider => ({
      value: provider.uuid,
      label: formatProviderLabel(provider),
      // Names the row the picker preselected, so the choice reads as a
      // configured default rather than an arbitrary one.
      ...(defaultProvider && provider.uuid === defaultProvider.uuid
        ? { tag: i18n.defaultTag }
        : {}),
    })),
  ];

  const handleChange = event => {
    const uuid = event.target.value;
    onChange(uuid, providers.find(provider => provider.uuid === uuid) || null);
  };

  return (
    <Select
      fullWidth
      label={label || i18n.selectProvider}
      value={loading ? '' : value || ''}
      onChange={handleChange}
      options={options}
      disabled={disabled || loading || error}
      error={error ? i18n.genericError : undefined}
    />
  );
};

export default ProviderSelect;
